/**
 * Product Media Swiper
 * Handles variant-specific media display with swiper functionality.
 *
 * Media source priority (first non-empty wins), provided by `snippets/product-media-swiper.liquid`:
 * 1) Variant list.file_reference metafield (`custom.vairnat_media` / `custom.variant_media`)
 *    via `script[data-all-variant-file-media="<product.id>"]`
 * 2) Legacy `custom.media_controls` via `script[data-all-media-controls="<product.id>"]`
 * 3) Standard Shopify `product.media` via `script[data-shopify-product-media="<product.id>"]`
 *
 * @version 2.7.0 - ADD: Prefer variant file-reference metafield before media_controls / product.media
 *   - Preload first media per variant after page load (idle + network-safe)
 *   - Videos pause/resume on slide change instead of resetting to start
 *   - Priority loading: slide 1 loads first for fastest perceived performance
 *   - Sequential loading for remaining slides guarantees correct order
 */

var _swiper_ver = '2.7.0';

class ProductMediaSwiper {
  constructor() {
    this.swipers = new Map();
    this.mediaCache = new Map();
    this.currentVariants = new Map();
    this.preloadedAssets = new Set();
    this.touchStartY = 0;
    this.touchStartX = 0;
    this.isScrollLocked = false;
    this.scrollLockPosition = 0;
    this.scrollAccumulator = 0;
    this.scrollThreshold = 30; // Pixels of scroll needed to change slide (single scroll wheel tick ~15-20px)
    this.lastScrollTime = 0;
    this.scrollDebounceTime = 300; // ms between slide changes (cooldown to prevent multi-slide jumps on a single fast scroll)
    this.globalScrollHandler = null;
    this.currentWheelSwiper = null;
    this.swiperScrollTargets = new Set();
    this.pageTopTolerance = 5; // px tolerance for checking if page is at absolute top
    this.minVisiblePixels = 120; // minimum hero visibility before hijacking scroll

    // Viewport tracking for scroll re-entry fix
    // Each swiper stores its own visibility state; this is the shared observer
    this.swiperObserver = null;

    this.init();
  }

  /**
   * Returns an incrementing token stored on the swiper instance.
   * Used to cancel stale async work (e.g. when variants change mid-load).
   */
  nextLoadToken(swiper) {
    swiper._loadToken = (swiper._loadToken || 0) + 1;
    return swiper._loadToken;
  }

  isLoadTokenActive(swiper, token) {
    return !!swiper && swiper._loadToken === token;
  }

  init() {
    // Initialize all product media galleries on the page
    document.querySelectorAll('.pdd-swiper-container').forEach(container => {
      this.initializeSwiper(container);
    });

    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail);
    });

    // Listen for product form updates
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="id"], select[name="id"]')) {
        this.handleVariantFormChange(event.target);
      }
    });
    
    // Listen for window resize to manage mobile dots class
    window.addEventListener('resize', () => {
      this.handleWindowResize();
    });
  }

  initializeSwiper(container) {
    const productId = container.dataset.productId;

    if (!productId) {
      console.error('❌ No product ID found');
      return;
    }

    // Check if already initialized
    if (this.swipers.has(productId)) {
      return;
    }

    // Get media controls data
    const mediaControlsScript = document.querySelector(`script[data-media-controls="${productId}"]`);
    const variantOptionsScript = document.querySelector(`script[data-variant-options="${productId}"]`);
    
    if (!mediaControlsScript || !variantOptionsScript) {
      this.showError(container, 'Media configuration not found');
      return;
    }

    let mediaControls, variantOptions;
    try {
      mediaControls = JSON.parse(mediaControlsScript.textContent);
      variantOptions = JSON.parse(variantOptionsScript.textContent);
    } catch (error) {
      console.error('Error parsing media data:', error);
      this.showError(container, 'Invalid media configuration');
      return;
    }

    // Get configuration options from gallery element
    const gallery = container.closest('.product-media-gallery');
    const enableScrollLock = gallery?.dataset.enableScrollLock === 'true';
    const loopSliding = gallery?.dataset.loopSliding === 'true';
    const enablePageScrollLock = gallery?.dataset.enablePageScrollLock === 'true';

    // Initialize swiper instance
    const swiperData = {
      container,
      productId,
      currentSlide: 0,
      slides: [],
      lastSlideChangeAt: 0,
      pendingUnlockTimeout: null,
      mediaControls,
      variantOptions,
      dots: container.parentNode.querySelector('.pdd-swiper-dots'),
      counter: container.parentNode.querySelector('.pdd-swiper-counter'),
      currentCounter: container.parentNode.querySelector('.pdd-swiper-current'),
      totalCounter: container.parentNode.querySelector('.pdd-swiper-total'),
      isLoading: false,
      lastVariantId: variantOptions.variant_id,
      // New configuration options
      enableScrollLock,
      loopSliding,
      enablePageScrollLock,
      galleryElement: gallery || container
    };

    this.swipers.set(productId, swiperData);
    this.currentVariants.set(productId, variantOptions);

    // Load and display media
    this.loadMediaForVariant(productId, variantOptions);

    // Setup event listeners
    this.setupEventListeners(swiperData);

    // Preload the first slide for each variant after the page finishes loading
    const runPreload = () => this.preloadFirstSlidesForAllVariants(swiperData);
    if (document.readyState === 'complete') {
      runPreload();
    } else {
      window.addEventListener('load', runPreload, { once: true });
    }

  }

  async loadMediaForVariant(productId, variantOptions) {
    const swiper = this.swipers.get(productId);
    if (!swiper) {
      console.error('❌ No swiper found for product ID:', productId);
      return;
    }

    const loadToken = this.nextLoadToken(swiper);
    this.clearGhostSlides(swiper);
    this.setLoadingState(swiper, true, true);

    try {
      const resolvedMedia = this.resolveMediaForVariant(productId, variantOptions?.variant_id);
      if (!resolvedMedia || resolvedMedia.length === 0) {
        this.showError(swiper.container, 'No media available for selected variant');
        return;
      }

      swiper.mediaControls = resolvedMedia;
      const sortedMedia = this.sortMedia(resolvedMedia);

      if (!sortedMedia || sortedMedia.length === 0) {
        this.showError(swiper.container, 'No media available for this product');
        return;
      }

      // Progressive render: create slide shells immediately, then hydrate slide 1.
      this.renderSlideShells(swiper, sortedMedia);

      const firstHydrated = await this.hydrateSlide(swiper, 0, loadToken);
      if (!firstHydrated) return; // cancelled or missing
      if (!this.isLoadTokenActive(swiper, loadToken)) return;
      
      // Mark ready: hide spinner as soon as first slide is ready.
      this.setLoadingState(swiper, false, true);

      this.updateIndicators(swiper);
      this.initializeFirstSlide(swiper);

      // Hydrate remaining slides in the background (do not block first paint).
      void this.hydrateRemainingSlides(swiper, loadToken);
      
    } catch (error) {
      console.error('Error loading media:', error);
      this.showError(swiper.container, 'Failed to load media');
    } finally {
      // Only clean up if this load is still the active one.
      if (this.isLoadTokenActive(swiper, loadToken)) {
        this.setLoadingState(swiper, false, true);
      }
    }
  }

  filterMediaByVariant(mediaControls, variantId) {
    const filtered = [];

    if (!mediaControls || mediaControls.length === 0) {
      return filtered;
    }

    // Filter by variant_id since media is stored per variant
    for (const media of mediaControls) {
      // Check if media has "main" role
      let hasMainRole = false;
      if (media.roles && media.roles.length > 0) {
        hasMainRole = media.roles.includes('main');
      }

      // Skip if not main role
      if (!hasMainRole) {
        continue;
      }

      // Check if media belongs to current variant
      const variantIdMatch = media.variant_id && String(media.variant_id) === String(variantId);

      if (variantIdMatch) {
        filtered.push({
          ...media,
          calculatedScore: media.order || 0
        });
      }
    }

    return filtered;
  }

  sortMedia(mediaArray) {
    // NEW: Simple sort by order field since file order in JSON determines display order
    return mediaArray.sort((a, b) => {
      const orderA = parseInt(a.order) || 0;
      const orderB = parseInt(b.order) || 0;
      return orderA - orderB;
    });
  }

  /**
   * Priority loading: Load slide 1 first for fastest perceived performance,
   * then load remaining slides sequentially to guarantee correct order.
   * This eliminates race conditions where faster-loading images would
   * appear before slower-loading videos, causing randomized slide order.
   */
  async preloadMedia(mediaArray) {
    const slides = [];

    if (mediaArray.length === 0) {
      return slides;
    }

    // Load first slide with priority - users see this immediately
    const firstMedia = mediaArray[0];
    try {
      const firstElement = await this.createMediaElement(firstMedia);
      slides.push({
        element: firstElement,
        media: firstMedia,
        isVideo: firstMedia.isVideo
      });
    } catch (error) {
      console.warn('Failed to load first slide:', firstMedia.file, error);
    }

    // Load remaining slides sequentially to guarantee order
    for (let i = 1; i < mediaArray.length; i++) {
      const media = mediaArray[i];
      try {
        const element = await this.createMediaElement(media);
        slides.push({
          element,
          media,
          isVideo: media.isVideo
        });
      } catch (error) {
        console.warn(`Failed to load slide ${i + 1}:`, media.file, error);
      }
    }

    return slides;
  }

  /**
   * Render empty slide wrappers ("shells") immediately so:
   * - indicators can show correct total slide count right away
   * - scroll/touch logic sees the correct number of slides
   * Media elements are injected later by `hydrateSlide`.
   */
  renderSlideShells(swiper, mediaArray, opts = {}) {
    const { container } = swiper;
    const preserveGhosts = opts && opts.preserveGhosts;
    if (!preserveGhosts) {
      container.innerHTML = '';
    } else {
      container.querySelectorAll('.pdd-swiper-slide').forEach((el) => {
        if (!el.classList.contains('pdd-swiper-slide-ghost')) el.remove();
      });
    }

    swiper.currentSlide = 0;
    swiper.slides = mediaArray.map((media, index) => {
      const slideElement = document.createElement('div');
      slideElement.className = `pdd-swiper-slide ${index === 0 ? 'is-active' : ''}`;
      container.appendChild(slideElement);

      return {
        element: slideElement, // element is always the wrapper DIV for transforms
        slideElement,
        mediaElement: null,
        media,
        isVideo: !!media.isVideo
      };
    });
  }

  /**
   * Hydrate a single slide by loading its media and injecting it into the slide shell.
   * Returns false when cancelled or slide missing.
   */
  async hydrateSlide(swiper, index, loadToken) {
    if (!this.isLoadTokenActive(swiper, loadToken)) return false;
    const slide = swiper.slides?.[index];
    if (!slide || !slide.slideElement) return false;
    if (slide.mediaElement) return true; // already hydrated

    try {
      const el = await this.createMediaElement(slide.media);
      if (!this.isLoadTokenActive(swiper, loadToken)) return false;

      // Replace any existing content
      slide.slideElement.innerHTML = '';
      slide.slideElement.appendChild(el);
      slide.mediaElement = el;

      // Ensure videos don't unexpectedly play when inserted off-screen.
      if (slide.isVideo && el && el.tagName === 'VIDEO') {
        el.autoplay = false;
        if (index === swiper.currentSlide) {
          const p = el.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else {
          try { el.pause(); } catch (e) {}
        }
      }

      return true;
    } catch (e) {
      if (!this.isLoadTokenActive(swiper, loadToken)) return false;

      // For videos, do NOT fall back to posters/images in this theme.
      // If the browser can't play any provided video source, show a lightweight error placeholder.
      if (slide?.isVideo) {
        const placeholder = document.createElement('div');
        placeholder.className = 'pdd-media-error';
        placeholder.innerHTML = `<p>Failed to load ${slide?.media?.file || 'video'}</p>`;
        slide.slideElement.innerHTML = '';
        slide.slideElement.appendChild(placeholder);
        slide.mediaElement = placeholder;
        return true;
      }

      // Images: basic fallback to the original URL if available.
      const fallbackUrl = slide?.media?.url;
      if (fallbackUrl) {
        const img = document.createElement('img');
        img.src = fallbackUrl;
        img.alt = slide?.media?.alt || 'Product media';
        img.loading = 'eager';
        img.decoding = 'async';
        slide.slideElement.innerHTML = '';
        slide.slideElement.appendChild(img);
        slide.mediaElement = img;
        return true;
      }
      return false;
    }
  }

  /**
   * Hydrate remaining slides sequentially to preserve order and avoid bandwidth spikes.
   * Runs in background; stops automatically if a new load starts (token changes).
   */
  async hydrateRemainingSlides(swiper, loadToken) {
    if (!swiper?.slides?.length) return;
    for (let i = 1; i < swiper.slides.length; i++) {
      if (!this.isLoadTokenActive(swiper, loadToken)) return;
      await this.hydrateSlide(swiper, i, loadToken);
    }
  }

  createMediaElement(mediaData) {
    return new Promise((resolve, reject) => {
      // Fix protocol-relative URLs
      let finalUrl = mediaData.url;
      if (finalUrl && finalUrl.startsWith('//')) {
        finalUrl = 'https:' + finalUrl;
      }

      const element = document.createElement(mediaData.isVideo ? 'video' : 'img');

      // Resolve exactly once (important for browsers that may fire multiple readiness events)
      let didResolve = false;
      const resolveOnce = (el) => {
        if (didResolve) return;
        didResolve = true;
        resolve(el);
      };

      // IMPORTANT (Safari/iOS): videos often won't fire `loadeddata` until playback starts.
      // If we wait for `loadeddata`, we can deadlock (we only call play() after hydration resolves).
      if (mediaData.isVideo) {
        element.addEventListener('loadedmetadata', () => resolveOnce(element), { once: true });
        element.addEventListener('loadeddata', () => resolveOnce(element), { once: true });
        element.addEventListener('canplay', () => resolveOnce(element), { once: true });

        // Safety net (Safari/iOS): some devices won’t preload video enough to fire readiness events
        // until playback starts. We still want to render immediately and attempt playback after hydration.
        setTimeout(() => resolveOnce(element), 500);
      } else {
        element.addEventListener('load', () => resolveOnce(element), { once: true });
      }

      element.onerror = (error) => {
        console.warn(`❌ Failed to load media: ${mediaData.file}`, mediaData);
        console.warn(`Error details:`, error);
        // Create a placeholder element as final fallback (no poster/image fallback in this theme).
        const placeholder = document.createElement('div');
        placeholder.style.cssText = `
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, #ffebee, #f3e5f5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
          font-size: 14px;
          text-align: center;
        `;
        placeholder.innerHTML = `<div>❌<br>Failed to load<br>${mediaData.file}</div>`;
        resolveOnce(placeholder);
      };
      
      if (mediaData.isVideo) {
        // Do not autoplay by default; we only play the active slide (see handleVideoPlayback / hydrateSlide).
        element.autoplay = false;
        element.loop = true;
        element.muted = true;
        element.playsInline = true;
        // iOS Safari prefers explicit attributes for inline playback.
        element.setAttribute('muted', '');
        element.setAttribute('loop', '');
        element.setAttribute('playsinline', '');
        element.setAttribute('webkit-playsinline', '');
        element.controls = false;
        element.preload = 'metadata';

        // Shopify-native: provide multiple sources (MP4/HLS/etc.) so each browser can choose.
        // If `sources` is missing, fall back to `url`.
        const sources = Array.isArray(mediaData.sources) ? mediaData.sources : null;
        if (sources && sources.length) {
          // Do NOT set element.src when using <source> children.
          for (let i = 0; i < sources.length; i++) {
            const s = sources[i];
            if (!s || !s.url) continue;
            let sUrl = s.url;
            if (sUrl && sUrl.startsWith('//')) sUrl = 'https:' + sUrl;
            const sourceEl = document.createElement('source');
            sourceEl.src = sUrl;
            if (s.type) sourceEl.type = s.type;
            element.appendChild(sourceEl);
          }
        } else if (finalUrl) {
          element.src = finalUrl;
        }

        // Hint Safari/iOS to start loading metadata immediately.
        try { element.load(); } catch (e) {}
        
        // Add error handling for video loading
        element.addEventListener('error', (e) => {
          console.warn('Video load error:', e, mediaData);
          // No poster/image fallback for videos in this theme. Let the generic placeholder handle it.
          reject(e);
        });
      } else {
        element.loading = 'eager';
        element.decoding = 'async';
        element.src = finalUrl;

        if (mediaData.srcset) {
          // Fix protocol-relative URLs in srcset WITHOUT corrupting absolute https:// URLs
          let srcset = mediaData.srcset;
          if (srcset && srcset.includes('//')) {
            // Only replace protocol-relative occurrences (start/comma/whitespace + //)
            srcset = srcset.replace(/(^|\s|,)(\/\/)/g, '$1https://');
          }
          element.srcset = srcset;
          element.sizes = mediaData.sizes;
        }
        
        element.alt = mediaData.alt || `Product media ${mediaData.file}`;
        
      }
      
      // Set timeout for loading
      setTimeout(() => {
        reject(new Error(`Timeout loading ${mediaData.file}`));
      }, 15000); // Increased timeout for better reliability
    });
  }

  renderSlides(swiper) {
    const {container, slides} = swiper;

    // Clear existing slides
    container.innerHTML = '';

    if (slides.length === 0) {
      console.error('❌ No slides to render');
      this.showError(container, 'No media available');
      return;
    }

    // Create slide elements
    slides.forEach((slide, index) => {
      const slideElement = document.createElement('div');
      slideElement.className = `pdd-swiper-slide ${index === 0 ? 'is-active' : ''}`;

      // Clone the media element properly
      const mediaElement = slide.element.cloneNode(true);
      slideElement.appendChild(mediaElement);
      container.appendChild(slideElement);

      // CRITICAL: Update slide.element to reference the wrapper DIV
      // This allows touch handlers to apply transforms to the correct element
      slide.slideElement = slideElement; // Store wrapper DIV
      slide.mediaElement = slide.element; // Keep original media element reference
      slide.element = slideElement; // Update element to point to wrapper DIV for transform application
    });
  }

  setupEventListeners(swiper) {
    const { container, productId, enablePageScrollLock } = swiper;

    // Enhanced touch handling for mobile
    let touchState = {
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      deltaX: 0,
      deltaY: 0,
      direction: null,
      isScrolling: false,
      isSwiping: false,
      startTime: 0
    };

    container.addEventListener('touchstart', (e) => {
      if (swiper.slides.length <= 1) return;

      touchState.startX = e.touches[0].clientX;
      touchState.startY = e.touches[0].clientY;
      touchState.currentX = touchState.startX;
      touchState.currentY = touchState.startY;
      touchState.startTime = Date.now();
      touchState.direction = null;
      touchState.isScrolling = false;
      touchState.isSwiping = false;

      container.classList.add('is-touching');
    }, { passive: false });

    container.addEventListener('touchmove', (e) => {
      if (swiper.slides.length <= 1) return;
      
      touchState.currentX = e.touches[0].clientX;
      touchState.currentY = e.touches[0].clientY;
      touchState.deltaX = touchState.currentX - touchState.startX;
      touchState.deltaY = touchState.currentY - touchState.startY;

      const absDeltaX = Math.abs(touchState.deltaX);
      const absDeltaY = Math.abs(touchState.deltaY);

      // Determine direction on first significant movement
      if (!touchState.direction && (absDeltaX > 10 || absDeltaY > 10)) {
        if (absDeltaX > absDeltaY) {
          touchState.direction = 'horizontal';
          touchState.isSwiping = true;
          container.classList.add('is-swiping');
          container.classList.add('is-dragging');

          // Prevent page scroll during horizontal swipe
          e.preventDefault();

          // Lock body scroll temporarily
          if (enablePageScrollLock) {
            document.body.style.overflow = 'hidden';
          }
        } else {
          touchState.direction = 'vertical';
          touchState.isScrolling = true;
        }
      }

      // Update drag animation for horizontal swipes
      if (touchState.direction === 'horizontal') {
        e.preventDefault();
        this.updateDragAnimation(swiper, touchState.deltaX);
      }
    }, { passive: false });

    container.addEventListener('touchend', (e) => {
      if (swiper.slides.length <= 1) {
        // Single tap on single slide - open modal
        if (!touchState.isSwiping && !touchState.isScrolling) {
          this.openModal(swiper);
        }
        this.resetTouchState(container, enablePageScrollLock);
        return;
      }

      const touchDuration = Date.now() - touchState.startTime;
      const absDeltaX = Math.abs(touchState.deltaX);
      const absDeltaY = Math.abs(touchState.deltaY);
      const containerWidth = container.offsetWidth;

      if (touchState.direction === 'horizontal') {
        // Instagram-style: Check if swipe passed threshold (20% of screen width for easier swiping)
        const swipeThreshold = containerWidth * 0.15; // 15% threshold (reduced from 33% for better UX)
        const shouldChangeslide = absDeltaX > swipeThreshold;

        if (shouldChangeslide) {
          // Passed threshold - complete the swipe
          // Skip animation on mobile - user already saw drag animation
          const isMobile = window.innerWidth <= 991;
          container.classList.add('is-snapping');

          if (touchState.deltaX > 0) {
            this.prevSlide(swiper, isMobile); // Skip animation on mobile
          } else {
            this.nextSlide(swiper, isMobile); // Skip animation on mobile
          }

          // Remove snapping class and reset touch state after animation completes
          setTimeout(() => {
            container.classList.remove('is-snapping');
            this.resetTouchState(container, enablePageScrollLock);
          }, 300);

          return; // Don't call resetTouchState immediately - wait for animation
        } else {
          // Didn't pass threshold - snap back to current slide
          this.snapBackToCurrentSlide(swiper);

          // Delay resetTouchState until snap back animation completes (300ms)
          // to keep is-dragging class active for CSS visibility rules
          setTimeout(() => {
            this.resetTouchState(container, enablePageScrollLock);
          }, 300);
          return; // Don't call resetTouchState immediately
        }
      } else if (!touchState.isSwiping && !touchState.isScrolling && touchDuration < 300) {
        // Quick tap - open modal
        this.openModal(swiper);
      }

      this.resetTouchState(container, enablePageScrollLock);
    }, { passive: false });

    // Click event for desktop
    container.addEventListener('click', (e) => {
      // Don't open modal if this was a scroll/wheel interaction
      if (swiper.lastWheelTime && Date.now() - swiper.lastWheelTime < 200) {
        return;
      }
      this.openModal(swiper);
    });

    // Setup global wheel handler for desktop scroll control
    if (enablePageScrollLock) {
      this.setupGlobalScrollHandler(swiper);
    }

    // Keyboard navigation
    container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prevSlide(swiper);
      if (e.key === 'ArrowRight') this.nextSlide(swiper);
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openModal(swiper);
      }
    });

    // Make container focusable for keyboard navigation
    container.setAttribute('tabindex', '0');
  }


  resetTouchState(container, enablePageScrollLock) {
    container.classList.remove('is-touching', 'is-swiping', 'is-dragging');

    // Clear drag classes and transforms from slides
    const slides = container.querySelectorAll('.pdd-swiper-slide');
    const slidesArray = Array.from(slides);

    // Manual array access to prevent Shopify minifier bugs - accessing each index directly
    for (let i = 0; i < slidesArray.length; i++) {
      const slide = slidesArray[i];
      if (slide) {
        slide.classList.remove('drag-prev', 'drag-next');
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.visibility = '';
      }
    }

    // Restore page scroll
    if (enablePageScrollLock) {
      document.body.style.overflow = '';
    }
  }

  updateDragAnimation(swiper, deltaX) {
    // Instagram-style drag animation: slides follow finger horizontally
    const { slides, currentSlide, container } = swiper;
    const totalSlides = slides.length;
    const containerWidth = container.offsetWidth;

    // Determine which slides to show based on drag direction
    let prevSlideIndex, nextSlideIndex;

    if (this.shouldUseLoopSliding(swiper)) {
      // Loop mode: wrap around
      prevSlideIndex = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
      nextSlideIndex = (currentSlide + 1) % totalSlides;
    } else {
      // Non-loop mode: clamp at edges
      prevSlideIndex = Math.max(0, currentSlide - 1);
      nextSlideIndex = Math.min(totalSlides - 1, currentSlide + 1);
    }

    // Extract DOM elements from slide objects
    // slides is an array of {element, media, isVideo} objects
    const currentSlideEl = slides[currentSlide]?.element;
    const prevSlideEl = slides[prevSlideIndex]?.element;
    const nextSlideEl = slides[nextSlideIndex]?.element;

    if (!currentSlideEl || !currentSlideEl.style) {
      console.error('❌ currentSlideEl is invalid!', {
        exists: !!currentSlideEl,
        hasStyle: currentSlideEl && !!currentSlideEl.style,
        type: currentSlideEl ? currentSlideEl.constructor.name : 'undefined'
      });
      return;
    }

    // deltaX > 0 means dragging right (showing prev slide from left)
    // deltaX < 0 means dragging left (showing next slide from right)

    // Reset all slides - use for loop to avoid Shopify minifier converting to forEach
    for (let i = 0; i < slides.length; i++) {
      const slideEl = slides[i]?.element;
      if (slideEl && slideEl.classList) {
        slideEl.classList.remove('drag-prev', 'drag-next');
        if (slideEl !== currentSlideEl) {
          slideEl.style.transform = '';
          slideEl.style.opacity = '0';
        }
      }
    }

    // Current slide follows finger
    const currentTransform = `translateX(${deltaX}px)`;
    currentSlideEl.style.transform = currentTransform;
    currentSlideEl.style.opacity = '1';

    if (deltaX > 0 && prevSlideIndex !== currentSlide && prevSlideEl && prevSlideEl.style) {
      // Dragging right - show previous slide coming from left
      prevSlideEl.classList.add('drag-prev');
      const prevTransform = `translateX(${deltaX - containerWidth}px)`;
      prevSlideEl.style.transform = prevTransform;
      prevSlideEl.style.opacity = '1';
    } else if (deltaX < 0 && nextSlideIndex !== currentSlide && nextSlideEl && nextSlideEl.style) {
      // Dragging left - show next slide coming from right
      nextSlideEl.classList.add('drag-next');
      const nextTransform = `translateX(${deltaX + containerWidth}px)`;
      nextSlideEl.style.transform = nextTransform;
      nextSlideEl.style.opacity = '1';
    }
  }

  snapBackToCurrentSlide(swiper) {
    // Animate slides back to their original position
    const { slides, currentSlide, container } = swiper;
    const containerWidth = container.offsetWidth;

    container.classList.add('is-snapping');

    // Find which slide has drag-prev or drag-next class to animate it offscreen
    for (let i = 0; i < Math.min(slides.length, 20); i++) {
      const slideEl = slides[i]?.element;
      if (!slideEl || !slideEl.style) continue;

      if (i === currentSlide) {
        // Current slide: return to center
        slideEl.style.transform = 'translateX(0)';
        slideEl.style.opacity = '1';
        slideEl.style.visibility = 'visible';
      } else if (slideEl.classList.contains('drag-prev')) {
        // Previous slide: animate offscreen to the left
        slideEl.style.transform = `translateX(-${containerWidth}px)`;
        slideEl.style.opacity = '1';
        slideEl.style.visibility = 'visible';
        // Keep drag-prev class for CSS visibility during animation
      } else if (slideEl.classList.contains('drag-next')) {
        // Next slide: animate offscreen to the right
        slideEl.style.transform = `translateX(${containerWidth}px)`;
        slideEl.style.opacity = '1';
        slideEl.style.visibility = 'visible';
        // Keep drag-next class for CSS visibility during animation
      } else {
        // Other slides: hide immediately
        slideEl.style.transform = '';
        slideEl.style.opacity = '';
        slideEl.style.visibility = '';
      }
    }

    // Remove snapping class after animation completes
    setTimeout(() => {
      container.classList.remove('is-snapping');
    }, 300);
  }

  shouldLockScroll(swiper) {
    // For desktop, lock scroll only if not on the last slide
    const isDesktop = window.innerWidth >= 992;

    if (!isDesktop) {
      // On mobile, we don't lock page scroll (only handle horizontal swipes)
      return false;
    }

    // On desktop, lock scroll if not on the last slide
    const isOnLastSlide = swiper.currentSlide === swiper.slides.length - 1;
    return !isOnLastSlide;
  }

  handleSwipeGesture(swiper, startX, endX, startY, endY) {
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const minSwipeDistance = 50;

    // Ignore if vertical scroll is more significant
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    if (Math.abs(deltaX) >= minSwipeDistance) {
      if (deltaX > 0) {
        this.prevSlide(swiper);
      } else {
        this.nextSlide(swiper);
      }
    }
  }

  nextSlide(swiper, skipAnimation = false) {
    if (swiper.slides.length <= 1) return;

    // Track navigation direction for mobile swipe completion.
    // This is critical for loop wrap-around (last → first) because index comparisons break there.
    swiper.lastNavDirection = 'next';

    // Check if we should use loop sliding based on configuration
    const shouldLoop = this.shouldUseLoopSliding(swiper);

    if (shouldLoop) {
      // Endless swiping: loop back to start when reaching end
      const nextIndex = (swiper.currentSlide + 1) % swiper.slides.length;
      this.goToSlide(swiper, nextIndex, skipAnimation);
    } else {
      // Non-loop: stop at the last slide
      const nextIndex = Math.min(swiper.currentSlide + 1, swiper.slides.length - 1);
      if (nextIndex !== swiper.currentSlide) {
        this.goToSlide(swiper, nextIndex, skipAnimation);
      }
    }
  }

  prevSlide(swiper, skipAnimation = false) {
    if (swiper.slides.length <= 1) return;

    // Track navigation direction for mobile swipe completion.
    // This is critical for loop wrap-around (first → last) because index comparisons break there.
    swiper.lastNavDirection = 'prev';

    // Check if we should use loop sliding based on configuration
    const shouldLoop = this.shouldUseLoopSliding(swiper);

    if (shouldLoop) {
      // Endless swiping: loop to end when going before start
      const prevIndex = swiper.currentSlide === 0 ? swiper.slides.length - 1 : swiper.currentSlide - 1;
      this.goToSlide(swiper, prevIndex, skipAnimation);
    } else {
      // Non-loop: stop at the first slide
      const prevIndex = Math.max(swiper.currentSlide - 1, 0);
      if (prevIndex !== swiper.currentSlide) {
        this.goToSlide(swiper, prevIndex, skipAnimation);
      }
    }
  }

  shouldUseLoopSliding(swiper) {
    // Determine device type - mobile devices get loop sliding, desktop doesn't
    const isMobile = window.innerWidth <= 991; // Using the same breakpoint as the CSS

    // Mobile devices: ALWAYS use loop sliding for endless swiping
    // Desktop: NEVER use loop sliding to allow page scroll after last slide
    return isMobile; // Always true on mobile, always false on desktop
  }

  goToSlide(swiper, index, skipAnimation = false) {
    if (index === swiper.currentSlide || index < 0 || index >= swiper.slides.length) {
      return;
    }

    const slides = swiper.container.querySelectorAll('.pdd-swiper-slide');
    const previousSlide = slides[swiper.currentSlide];
    const newSlide = slides[index];
    const previousIndex = swiper.currentSlide;

    if (skipAnimation) {
      // Mobile swipe completion - animate slides to their final positions
      // Same approach as snapBackToCurrentSlide: keep drag classes during animation
      const containerWidth = swiper.container.offsetWidth;
      // IMPORTANT: Determine direction based on the actual navigation intent, not index comparison.
      // Index comparison fails for loop wrap-around (e.g. 3/3 → 1/3) and causes the "rubberband" reversal.
      const navDir = swiper.lastNavDirection; // 'next' | 'prev' (set by nextSlide/prevSlide)
      const isSwipingLeft = navDir === 'next'
        ? true
        : navDir === 'prev'
          ? false
          : index > swiper.currentSlide;

      // Add snapping class for smooth transition
      swiper.container.classList.add('is-snapping');

      // Set up new slide as active (z-index: 2) and prepare for animation
      previousSlide?.classList.remove('is-active');
      newSlide?.classList.add('is-active');

      // CRITICAL FIX: Keep drag-prev/drag-next classes during animation
      // This prevents white background flash by keeping slides visible via CSS rules

      // Animate previous slide off-screen (stays visible during animation)
      if (previousSlide) {
        previousSlide.style.visibility = 'visible';
        previousSlide.style.opacity = '1'; // Keep visible during exit animation
        previousSlide.style.zIndex = '1'; // Behind the new slide

        // Animate off-screen in the direction of the swipe
        const exitTransform = isSwipingLeft ?
          `translateX(-${containerWidth}px)` :
          `translateX(${containerWidth}px)`;
        previousSlide.style.transform = exitTransform;

        // DON'T remove drag-prev/drag-next class yet! Let CSS keep it visible
      }

      // Animate new slide to center
      if (newSlide) {
        newSlide.style.transform = 'translateX(0)';
        newSlide.style.opacity = '1';
        newSlide.style.visibility = 'visible';

        // Keep drag-prev or drag-next class during animation
        // CSS rule .is-snapping .drag-prev/.drag-next keeps it visible
      }

      // After animation completes (300ms), clean up classes and styles
      setTimeout(() => {
        swiper.container.classList.remove('is-snapping');
        swiper.lastNavDirection = null;

        // Clean up previous slide
        if (previousSlide) {
          previousSlide.style.removeProperty('transform');
          previousSlide.style.removeProperty('opacity');
          previousSlide.style.removeProperty('visibility');
          previousSlide.style.removeProperty('z-index');
          previousSlide.classList.remove('drag-prev', 'drag-next'); // NOW remove classes
        }

        // Clean up new slide
        if (newSlide) {
          newSlide.style.removeProperty('transform');
          newSlide.style.removeProperty('opacity');
          newSlide.style.removeProperty('visibility');
          newSlide.classList.remove('drag-prev', 'drag-next'); // NOW remove classes
        }
      }, 300); // Match CSS transition duration
    } else {
      // Overlay transition approach to prevent white flash
      // Step 1: Mark new slide as transitioning in (will overlay current slide)
      newSlide?.classList.add('is-transitioning-in');

      // Step 2: Mark previous slide as transitioning out (stays visible underneath)
      previousSlide?.classList.add('is-transitioning-out');

      // Step 3: After animation completes, clean up classes
      setTimeout(() => {
        // Remove old active state and transition classes
        previousSlide?.classList.remove('is-active', 'is-transitioning-out');

        // Add active state and remove transition class from new slide
        newSlide?.classList.remove('is-transitioning-in');
        newSlide?.classList.add('is-active');
      }, 300); // Match --anim-t-default (300ms)
    }

    // Handle video playback
    this.handleVideoPlayback(swiper.container, previousSlide, newSlide);

    swiper.currentSlide = index;
    swiper.lastSlideChangeAt = Date.now();

    // Update page scroll lock state based on current slide (desktop only)
    this.updatePageScrollLock(swiper);

    // Check if we need a two-step animation for dots
    // This happens when moving from slide 2->3 or backwards from 3->2
    const needsTwoStepAnimation = this.needsTwoStepDotAnimation(previousIndex, index, swiper.slides.length);

    if (needsTwoStepAnimation) {
      // Step 1: Update active dot only (no window shift)
      this.updateIndicatorsActiveOnly(swiper);

      // Step 2: Shift the window after a more noticeable delay
      setTimeout(() => {
        this.updateIndicators(swiper);
      }, 400); // 400ms delay for clearer visual effect
    } else {
      // Normal update for other transitions
      this.updateIndicators(swiper);
    }

    // Reset scroll accumulator when changing slides
    this.scrollAccumulator = 0;
  }

  needsTwoStepDotAnimation(previousIndex, newIndex, totalSlides) {
    // DISABLED - causes visual confusion when active dot seems stuck
    // We'll do all updates in one step for clarity
    return false;
  }

  /**
   * Update dots and counter indicators with Instagram-style thumbwheel behavior
   */
  updateIndicators(swiper) {
    const { dots, counter, currentCounter, totalCounter, slides, currentSlide } = swiper;
    const totalSlides = slides.length;

    // Keep the counter width visually stable by reserving space for the maximum digit count.
    // Example: total=12 → reserve 2 digit slots for both current + total, so "1/12" doesn't shift.
    if (counter) {
      const totalDigits = String(totalSlides || 0).length || 1;
      counter.style.setProperty('--pdd-swiper-counter-digits', totalDigits);
    }

    // When only one (or zero) slide exists, hide both indicators entirely
    if (totalSlides <= 1) {
      if (counter) {
        counter.style.setProperty('display', 'none', 'important');
        counter.setAttribute('aria-hidden', 'true');
      }
      if (dots) {
        dots.style.setProperty('display', 'none', 'important');
        dots.setAttribute('aria-hidden', 'true');
        // Clear any existing dots to prevent rendering/misalignment logic from running
        if (dots.children.length) dots.innerHTML = '';
      }
      return; // No need to proceed with updates for single-slide case
    }

    // Ensure indicators are visible when multiple slides exist
    if (counter) {
      counter.style.removeProperty('display');
      counter.removeAttribute('aria-hidden');
    }
    if (dots) {
      dots.style.removeProperty('display');
      dots.removeAttribute('aria-hidden');
    }

    // Update counter
    if (currentCounter) currentCounter.textContent = currentSlide + 1;
    if (totalCounter) totalCounter.textContent = totalSlides;

    // Update dots with Instagram-style thumbwheel
    if (!dots) return;

    // Create dots if they don't exist yet
    if (dots.children.length !== totalSlides) {
      dots.innerHTML = '';
      for (let i = 0; i < totalSlides; i++) {
        const dot = document.createElement('button');
        dot.className = 'pdd-swiper-dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('data-slide-index', i);

        // Add click handler
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          this.goToSlide(swiper, i);
        });

        dots.appendChild(dot);
      }
    }

    // Apply Instagram-style dot states
    this.applyInstagramDotStates(dots, currentSlide, totalSlides);
  }

  /**
   * Apply Instagram-style dot scaling and positioning
   * Pattern for 7 visible dots: (50%, 75%, 100%, 100% active, 100%, 75%, 50%)
   * - Distance 0: Active dot at 100%
   * - Distance 1: Dots adjacent to active at 100%
   * - Distance 2: Medium distance dots at 75%
   * - Distance 3: Far edge dots at 50%
   */
  applyInstagramDotStates(dotsContainer, currentSlide, totalSlides) {
    const dotElements = Array.from(dotsContainer.children);

    // Special case: 3 or fewer slides - all dots full size, no scrolling
    if (totalSlides <= 3) {
      // Remove overflow class for ≤3 slides
      dotsContainer.classList.remove('pdd-swiper-dots--overflow');

      // Reset transform to default (no offset needed for ≤3 slides)
      dotsContainer.style.removeProperty('transform');

      dotElements.forEach((dot, index) => {
        dot.setAttribute('data-dot-state', 'center');
        if (index === currentSlide) {
          dot.classList.add('is-active');
        } else {
          dot.classList.remove('is-active');
        }
      });
      dotsContainer.style.textIndent = '0px';
      return;
    }

    // For 4+ slides: Add overflow class for fixed-width window
    dotsContainer.classList.add('pdd-swiper-dots--overflow');

    // For 4+ slides: Calculate window of 7 dots maximum
    // The active dot should be centered with up to 2 dots on each side
    const maxVisibleDots = 7;
    const centerPosition = Math.floor(maxVisibleDots / 2); // 3 (0-indexed)

    // Calculate which dots should be visible
    let windowStart, windowEnd;

    if (currentSlide < centerPosition) {
      // At the beginning: show first 7 dots
      windowStart = 0;
      windowEnd = Math.min(maxVisibleDots - 1, totalSlides - 1);
    } else if (currentSlide >= totalSlides - centerPosition) {
      // At the end: show last 7 dots
      windowEnd = totalSlides - 1;
      windowStart = Math.max(0, totalSlides - maxVisibleDots);
    } else {
      // Middle: center on current slide
      windowStart = currentSlide - centerPosition;
      windowEnd = currentSlide + centerPosition;
    }

    // Apply states to all dots
    dotElements.forEach((dot, index) => {
      const isActive = index === currentSlide;

      // Update active class
      if (isActive) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }

      // Determine dot state based on position relative to window
      let state;

      if (index < windowStart || index > windowEnd) {
        // Outside visible window
        state = 'hidden';
      } else {
        // Calculate distance from active slide
        const distanceFromActive = Math.abs(index - currentSlide);

        if (distanceFromActive === 0) {
          // Active dot - always full size
          state = 'center';
        } else if (distanceFromActive === 1) {
          // Dots immediately adjacent to active (±1) - also full size
          state = 'center'; // 100% scale (same as active)
        } else if (distanceFromActive === 2) {
          // Medium distance dots (±2 from active)
          state = 'adjacent'; // 75% scale
        } else if (distanceFromActive === 3) {
          // Far dots (±3 from active) - only at edges of 7-dot window
          state = 'far'; // 50% scale
        } else {
          // Beyond 3 positions away - should be hidden
          state = 'hidden';
        }
      }

      dot.setAttribute('data-dot-state', state);
    });

    // Calibrated translateX calculation
    // Based on visual testing with 15 slides, we know:
    // - Slide 1 should be at 65px
    // - Slide 8 should be at 0px
    // - Slide 15 should be at -65px
    //
    // The pattern follows a centering algorithm where the middle slide is at 0px
    // and slides at the edges have symmetric offsets

    let translateX;

    // Find the middle slide (anchor point where translateX = 0)
    const middleSlide = Math.floor((totalSlides - 1) / 2); // e.g., for 15 slides, this is 7 (index)

    if (totalSlides <= 3) {
      // For ≤3 slides, keep dots centered with minimal offset
      translateX = (1 - currentSlide) * 12;
    } else {
      // For 4+ slides, use calibrated offset per slide from middle
      // Based on 15 slides: from slide 0 to slide 7 is 65px difference over 7 steps ≈ 9.3px/slide
      // But the steps aren't linear - they're larger at the edges

      // Use a scaling factor based on total slides
      // For 15 slides, the edge offset is 65px
      // Scale proportionally for other slide counts
      const edgeOffset = Math.min(65, (totalSlides - 1) * 4.3); // Approximate scaling

      // Calculate offset from middle
      const offsetFromMiddle = currentSlide - middleSlide;

      // Non-linear scaling: larger steps near edges
      if (Math.abs(offsetFromMiddle) <= 3) {
        // Near center: ~11px per step
        translateX = -offsetFromMiddle * 11;
      } else {
        // Near edges: use edge offset with smaller incremental steps
        const sign = offsetFromMiddle > 0 ? -1 : 1;
        const absOffset = Math.abs(offsetFromMiddle);
        translateX = sign * (33 + (absOffset - 3) * 9);
      }
    }

    // Round to nearest pixel
    translateX = Math.round(translateX);

    // Apply transform
    // First time: disable transition inline, then enable it via class
    if (!dotsContainer.classList.contains('is-initialized')) {
      // Disable transition for first update
      dotsContainer.style.setProperty('transition', 'none', 'important');
      dotsContainer.style.setProperty('transform', `translateX(${translateX}px) translateZ(0)`, 'important');

      // Force reflow
      void dotsContainer.offsetHeight;

      // Re-enable transition and add initialized class
      dotsContainer.style.removeProperty('transition');
      dotsContainer.classList.add('is-initialized');
    } else {
      // Normal update with transition
      dotsContainer.style.setProperty('transform', `translateX(${translateX}px) translateZ(0)`, 'important');
    }

  }

  updateIndicatorsActiveOnly(swiper) {
    // Simple active state update (currently not used since two-step is disabled)
    const dotsContainer = swiper.dots;
    if (!dotsContainer) return;

    const dots = dotsContainer.children;
    const currentSlide = swiper.currentSlide;

    Array.from(dots).forEach((dot, index) => {
      if (index === currentSlide) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }

  handleVideoPlayback(container, previousSlide, newSlide) {
    // Pause all videos except the new active slide
    // Do NOT reset currentTime - videos resume from where they left off
    container.querySelectorAll('video').forEach(video => {
      // Only pause videos not in the new active slide
      if (!newSlide || !newSlide.contains(video)) {
        video.pause();
      }
    });
    
    // Handle new slide media - resume video from current position
    if (newSlide) {
      const video = newSlide.querySelector('video');
      if (video) {
        // Ensure video properties are set (loop, muted, playsInline)
        // but do NOT reset currentTime - let video resume where it was
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        
        // Resume playback from current position
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // Video autoplay prevented - this is expected behavior
          });
        }
      }
    }
  }

  handleVariantChange(detail) {
    const { productId, variantId, variant } = detail;
    const swiper = this.swipers.get(productId);
    
    if (!swiper) {
      console.warn('No swiper found for product', productId);
      return;
    }
    
    if (swiper.lastVariantId === variantId) {
      return;
    }

    // Update variant options
    const newVariantOptions = {
      variant_id: variantId,
      options: [
        variant.option1?.toLowerCase(),
        variant.option2?.toLowerCase(), 
        variant.option3?.toLowerCase()
      ].filter(Boolean)
    };

    swiper.lastVariantId = variantId;
    this.currentVariants.set(productId, newVariantOptions);
    
    // Reload media for the new variant
    this.reloadMediaForNewVariant(productId, variantId);
  }

  handleVariantFormChange(input) {
    const form = input.closest('form');
    if (!form) return;

    const productId = form.querySelector('[name="product-id"]')?.value;
    if (!productId) return;

    const variantId = input.value;
    const swiper = this.swipers.get(productId);
    
    if (!swiper || swiper.lastVariantId === variantId) return;

    // Get variant data - try multiple methods to find the variant info
    let variantData = null;
    
    // Method 1: Try global window.product object
    if (window.product && window.product.variants) {
      variantData = window.product.variants.find(v => v.id === parseInt(variantId));
    }
    
    // Method 1.5: Try to get current variant from page URL or stored data
    if (!variantData) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      if (urlVariantId === variantId && window.product?.variants) {
        variantData = window.product.variants.find(v => v.id === parseInt(variantId));
      }
    }
    
    // Method 2: Try to construct variant data from form elements or selection
    if (!variantData || (!variantData.option1 && !variantData.option2 && !variantData.option3)) {
      
      // Look for checked radio buttons, selected options, or active variant buttons
      // Try to find selected elements with different approaches
      let selectedElements = [];
      
      try {
        selectedElements = form.querySelectorAll('input[type="radio"]:checked');
      } catch (e) {
        console.warn('Error finding radio buttons:', e);
      }
      
      if (selectedElements.length === 0) {
        try {
          selectedElements = form.querySelectorAll('select option:selected');
        } catch (e) {
          console.warn('Error finding select options:', e);
        }
      }
      
      if (selectedElements.length === 0) {
        try {
          selectedElements = form.querySelectorAll('[aria-pressed="true"]');
        } catch (e) {
          console.warn('Error finding pressed elements:', e);
        }
      }
      
      const options = [];
      selectedElements.forEach(el => {
        let optionValue = el.textContent?.trim() || el.value?.trim() || el.dataset.variantOption || el.dataset.value;
        if (optionValue && optionValue !== variantId) {
          options.push(optionValue);
        }
      });
      
      // Also try to get from URL parameters if available
      const urlParams = new URLSearchParams(window.location.search);
      const currentVariantParam = urlParams.get('variant');
      
      // Construct variant data
      variantData = {
        id: parseInt(variantId),
        option1: options[0] || '',
        option2: options[1] || '',
        option3: options[2] || ''
      };
    }
    
    if (variantData && (variantData.option1 || variantData.option2 || variantData.option3)) {
      this.handleVariantChange({
        productId,
        variantId,
        variant: variantData
      });
    } else {
      console.error('❌ Could not determine variant data for', variantId);
      
      // As last resort, update URL and reload if needed
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('variant', variantId);
      
      if (currentUrl.toString() !== window.location.toString()) {
        window.history.replaceState({}, '', currentUrl);
        
        // Trigger a variant change event with minimal data
        this.handleVariantChange({
          productId,
          variantId,
          variant: { id: parseInt(variantId), option1: '', option2: '', option3: '' }
        });
      }
    }
  }

  getVariantData(variantId, form) {
    // Try to get variant data from various sources
    let variantData = null;
    
    // Method 1: Try global window.product object (common in Shopify themes)
    if (window.product && window.product.variants) {
      variantData = window.product.variants.find(v => v.id === parseInt(variantId));
    }
    
    // Method 2: Try to get from form elements
    if (!variantData && form) {
      const selectedOptions = {
        option1: '',
        option2: '',
        option3: ''
      };
      
      // Look for selected option values in various form elements
      const optionSelectors = form.querySelectorAll('input[name*="option"]:checked, select[name*="option"], input[type="radio"]:checked');
      optionSelectors.forEach(input => {
        if (input.name.includes('option1') || input.dataset.option === '1') {
          selectedOptions.option1 = input.value;
        } else if (input.name.includes('option2') || input.dataset.option === '2') {
          selectedOptions.option2 = input.value;
        } else if (input.name.includes('option3') || input.dataset.option === '3') {
          selectedOptions.option3 = input.value;
        }
      });
      
      variantData = selectedOptions;
    }
    
    // Method 3: Try to get from URL parameters or other sources
    if (!variantData || (!variantData.option1 && !variantData.option2 && !variantData.option3)) {
      // Try to get current selected variant info from page
      const variantIdInput = form?.querySelector('input[name="id"], select[name="id"]');
      if (variantIdInput && window.product) {
        const currentVariantId = parseInt(variantIdInput.value);
        variantData = window.product.variants?.find(v => v.id === currentVariantId);
      }
    }
    
    // Ensure we return the expected format
    return {
      option1: variantData?.option1 || '',
      option2: variantData?.option2 || '', 
      option3: variantData?.option3 || ''
    };
  }

  showError(container, message) {
    console.error('Media gallery error:', message);
    container.innerHTML = `
      <div class="pdd-media-error">
        <p>${message}</p>
        <p><small>Please check that media files are properly uploaded to Shopify and accessible.</small></p>
      </div>
    `;
    const swiper = Array.from(this.swipers.values()).find((s) => s.container === container);
    if (swiper) {
      this.setLoadingState(swiper, false, true);
    } else {
      container.removeAttribute('data-loading');
    }
  }

  openModal(swiper) {
    const { productId, currentSlide, slides } = swiper;
    
    if (!slides[currentSlide]) return;

    // Find the product modal
    const productModal = document.querySelector(`#ProductModal-${this.getSectionId()}`);
    if (!productModal) {
      console.warn('Product modal not found');
      return;
    }

    // Get current media info
    const currentMedia = slides[currentSlide].media;
    
    // Set the data-media-id on the container to tell modal which media to show
    swiper.container.setAttribute('data-media-id', `media-${productId}-${currentSlide}`);
    
    // Update modal content with current media
    this.updateModalContent(productModal, slides, currentSlide, productId);
    
    // Show the modal
    if (productModal.show) {
      productModal.show(swiper.container);
    } else {
      // Fallback for older modal implementation
      productModal.setAttribute('open', '');
      productModal.classList.add('media-modal--opened');
    }
  }

  updateModalContent(modal, slides, currentSlide, productId) {
    const modalContent = modal.querySelector('.product-media-modal__content');
    if (!modalContent) return;

    // Clear existing content
    modalContent.innerHTML = '';

    // Add all slides to modal
    slides.forEach((slide, index) => {
      const mediaWrapper = document.createElement('div');
      mediaWrapper.className = `product__media-item ${index === currentSlide ? 'active' : ''}`;
      mediaWrapper.setAttribute('data-media-id', `media-${productId}-${index}`);
      
      // Clone the media element
      const mediaElement = slide.element.cloneNode(true);
      mediaElement.loading = 'lazy';
      mediaElement.className = 'global-media-settings global-media-settings--no-shadow';
      
      // For videos in modal, enable controls
      if (slide.isVideo) {
        mediaElement.controls = true;
        mediaElement.autoplay = false;
      }
      
      mediaWrapper.appendChild(mediaElement);
      modalContent.appendChild(mediaWrapper);
    });
  }

  getSectionId() {
    // Try to find section ID from existing elements
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.dataset.section) {
      return productInfo.dataset.section;
    }
    
    // Fallback - look for existing modal
    const existingModal = document.querySelector('[id^="ProductModal-"]');
    if (existingModal) {
      return existingModal.id.replace('ProductModal-', '');
    }
    
    // Default fallback
    return 'template--product';
  }

  setLoadingState(swiper, isLoading, showSpinner = true) {
    if (!swiper || !swiper.container) return;
    swiper.isLoading = isLoading;

    if (swiper._loadingTimer) {
      clearTimeout(swiper._loadingTimer);
      swiper._loadingTimer = null;
    }

    if (!showSpinner) {
      swiper.container.removeAttribute('data-loading');
      return;
    }

    if (isLoading) {
      swiper._loadingTimer = setTimeout(() => {
        swiper.container.setAttribute('data-loading', 'true');
      }, 120);
    } else {
      swiper.container.removeAttribute('data-loading');
    }
  }

  clearGhostSlides(swiper) {
    if (!swiper || !swiper.container) return;
    swiper.container.querySelectorAll('.pdd-swiper-slide-ghost').forEach((el) => el.remove());
  }

  prepareVariantSwap(swiper) {
    if (!swiper || !swiper.container) return [];
    this.clearGhostSlides(swiper);

    const slides = Array.from(swiper.container.querySelectorAll('.pdd-swiper-slide'));
    if (!slides.length) return [];

    // Keep only the currently visible slide as the outgoing "ghost".
    // Converting every old slide to a visible ghost can surface non-active/empty slides
    // and cause a white flash during variant crossfade.
    const activeSlide =
      slides.find((slide) => slide.classList.contains('is-active')) ||
      slides[swiper.currentSlide] ||
      slides[0];

    slides.forEach((slide) => {
      if (slide !== activeSlide) return;

      slide.dataset.ghostWasActive = 'true';
      slide.classList.remove(
        'is-active',
        'is-transitioning-in',
        'is-transitioning-out',
        'drag-prev',
        'drag-next'
      );
      slide.classList.remove('pdd-swiper-slide');
      slide.classList.add('pdd-swiper-slide-ghost');
      slide.style.transform = '';
      slide.style.opacity = '1';
      slide.style.visibility = 'visible';
      slide.style.zIndex = '2';
    });

    return activeSlide ? [activeSlide] : [];
  }

  restoreGhostSlides(swiper) {
    if (!swiper || !swiper.container) return;
    swiper.container.querySelectorAll('.pdd-swiper-slide-ghost').forEach((slide) => {
      const wasActive = slide.dataset.ghostWasActive === 'true';
      slide.classList.remove('pdd-swiper-slide-ghost', 'is-ghost-fade');
      slide.classList.add('pdd-swiper-slide');
      slide.removeAttribute('data-ghost-was-active');
      if (wasActive) slide.classList.add('is-active');
      slide.style.removeProperty('transform');
      slide.style.removeProperty('opacity');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('z-index');
    });
  }

  finishVariantSwap(swiper) {
    if (!swiper || !swiper.container) return;
    const ghosts = Array.from(swiper.container.querySelectorAll('.pdd-swiper-slide-ghost'));
    if (!ghosts.length) return;

    // Keep the outgoing ghost fully visible while the incoming variant slide fades in.
    // Then remove the ghost once the overlay fade is complete to avoid white flicker.
    setTimeout(() => {
      ghosts.forEach((el) => el.remove());
    }, 300);
  }

  applyVariantFadeIn(swiper) {
    const slide = swiper?.slides?.[0]?.slideElement;
    if (!slide) return;
    slide.classList.remove('is-active');
    slide.classList.add('is-transitioning-in');
    setTimeout(() => {
      slide.classList.remove('is-transitioning-in');
      slide.classList.add('is-active');
    }, 300);
  }

  shouldPreloadVariantMedia() {
    try {
      const c = navigator.connection;
      if (!c) return true;
      if (c.saveData) return false;
      const type = String(c.effectiveType || '').toLowerCase();
      if (type.includes('2g') || type.includes('slow-2g')) return false;
      return true;
    } catch (_) {
      return true;
    }
  }

  waitForIdle() {
    return new Promise((resolve) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => resolve(), { timeout: 2000 });
      } else {
        setTimeout(resolve, 0);
      }
    });
  }

  getPreloadKey(media) {
    return String(media?.file || media?.url || '');
  }

  async preloadMediaAssets(list) {
    for (const media of list) {
      const key = this.getPreloadKey(media);
      if (!key || this.preloadedAssets.has(key)) continue;
      this.preloadedAssets.add(key);
      try {
        await this.createMediaElement(media);
      } catch (_) {}
      await this.waitForIdle();
    }
  }

  async preloadFirstSlidesForAllVariants(swiper) {
    try {
      if (!swiper || swiper._preloadFirstDone) return;
      swiper._preloadFirstDone = true;

      if (!this.shouldPreloadVariantMedia()) return;

      const variantIds = new Set();
      const fileMedia = this.getAllVariantFileMedia(swiper.productId) || [];
      const controlsMedia = this.getAllMediaControls(swiper.productId) || [];
      fileMedia.forEach((m) => {
        if (m?.variant_id != null) variantIds.add(String(m.variant_id));
      });
      controlsMedia.forEach((m) => {
        if (m?.variant_id != null) variantIds.add(String(m.variant_id));
      });

      const firstItems = [];
      for (const id of variantIds) {
        const list = this.sortMedia(this.resolveMediaForVariant(swiper.productId, id));
        if (list && list.length) firstItems.push(list[0]);
      }

      await this.preloadMediaAssets(firstItems);
    } catch (_) {}
  }

  getMediaSignature(items) {
    try {
      return (items || [])
        .map((m, i) => `${m.file || m.url || ''}|${m.order || i}`)
        .join('||');
    } catch (_) {
      return '';
    }
  }

  /**
   * Resolve media for a variant with fixed priority:
   * 1) variant file-reference metafield
   * 2) legacy media_controls
   * 3) standard Shopify product media
   */
  resolveMediaForVariant(productId, variantId) {
    const fileMedia = this.filterMediaByVariant(
      this.getAllVariantFileMedia(productId) || [],
      variantId
    );
    if (fileMedia.length) return fileMedia;

    const controlsMedia = this.filterMediaByVariant(
      this.getAllMediaControls(productId) || [],
      variantId
    );
    if (controlsMedia.length) return controlsMedia;

    return this.getShopifyProductMedia(productId) || [];
  }

  async reloadMediaForNewVariant(productId, variantId) {
    const swiper = this.swipers.get(productId);
    if (!swiper) return;

    const loadToken = this.nextLoadToken(swiper);
    const hasExistingSlides = !!(swiper.slides && swiper.slides.length);
    let swapPrepared = false;
    let swapCompleted = false;

    // Show loading state (skip spinner if we already have visible media)
    this.setLoadingState(swiper, true, !hasExistingSlides);
    
    try {
      const mediaForVariant = this.resolveMediaForVariant(productId, variantId);
      if (!mediaForVariant || mediaForVariant.length === 0) {
        console.warn('No media found for variant:', variantId);
        this.showError(swiper.container, 'No media found for selected variant');
        return;
      }

      if (!this.isLoadTokenActive(swiper, loadToken)) return;

      const currentSig = this.getMediaSignature(swiper.mediaControls);
      const nextSig = this.getMediaSignature(mediaForVariant);
      if (nextSig && nextSig === currentSig) {
        this.setLoadingState(swiper, false, !hasExistingSlides);
        return;
      }

      if (hasExistingSlides) {
        const ghosts = this.prepareVariantSwap(swiper);
        swapPrepared = ghosts.length > 0;
      } else {
        this.clearGhostSlides(swiper);
      }

      swiper.mediaControls = mediaForVariant;
      const sortedMedia = this.sortMedia(mediaForVariant);
      if (!sortedMedia || sortedMedia.length === 0) {
        this.showError(swiper.container, 'No media available for selected variant');
        return;
      }

      // Reset dots state for new variant (prevents centering bugs on count changes)
      if (swiper.dots) {
        swiper.dots.classList.remove('is-initialized');
        swiper.dots.classList.remove('pdd-swiper-dots--overflow');
        swiper.dots.style.removeProperty('transform');
        swiper.dots.style.removeProperty('text-indent');
        swiper.dots.innerHTML = '';
      }

      // Progressive render (same as initial load): shells → hydrate slide 1 → background hydrate rest
      this.renderSlideShells(swiper, sortedMedia, { preserveGhosts: swapPrepared });
      if (swapPrepared && swiper.slides?.[0]?.slideElement) {
        swiper.slides[0].slideElement.classList.remove('is-active');
      }
      const firstHydrated = await this.hydrateSlide(swiper, 0, loadToken);
      if (!firstHydrated) return;
      if (!this.isLoadTokenActive(swiper, loadToken)) return;

      this.setLoadingState(swiper, false, !hasExistingSlides);

      this.updateIndicators(swiper);
      this.initializeFirstSlide(swiper);
      if (swapPrepared) {
        this.applyVariantFadeIn(swiper);
        this.finishVariantSwap(swiper);
        swapCompleted = true;
      }
      void this.hydrateRemainingSlides(swiper, loadToken);
    } catch (error) {
      console.error('Error reloading media for new variant:', error);
      this.showError(swiper.container, 'Failed to load media for variant');
    } finally {
      if (this.isLoadTokenActive(swiper, loadToken)) {
        this.setLoadingState(swiper, false, !hasExistingSlides);
        if (swapPrepared && !swapCompleted) {
          this.restoreGhostSlides(swiper);
        }
      }
    }
  }

  getAllVariantFileMedia(productId) {
    const el = document.querySelector(`script[data-all-variant-file-media="${productId}"]`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('Error parsing all variant file media:', error);
      return null;
    }
  }

  getAllMediaControls(productId) {
    // Try to get all media controls from a data attribute or global variable
    // This would need to be set up to include all media for all variants
    const allMediaScript = document.querySelector(`script[data-all-media-controls="${productId}"]`);
    if (allMediaScript) {
      try {
        return JSON.parse(allMediaScript.textContent);
      } catch (error) {
        console.error('Error parsing all media controls:', error);
      }
    }
    return null;
  }

  /**
   * Read standard Shopify product media and convert to our internal structure.
   * Preserves Shopify admin ordering (array order).
   */
  getShopifyProductMedia(productId) {
    // 0) Preferred: theme-provided JSON (stable, preserves admin order)
    try {
      if (productId) {
        const el = document.querySelector(`script[type="application/json"][data-shopify-product-media="${productId}"]`);
        if (el) {
          const parsed = JSON.parse(el.textContent || '[]');
          if (Array.isArray(parsed) && parsed.length) return parsed;
        }
      }
    } catch (e) {
      console.warn('Error parsing data-shopify-product-media', e);
    }

    const toHttps = (u) => (u && u.startsWith('//') ? `https:${u}` : u);
    const buildImage = (src, index, alt, title) => {
      const url = toHttps(src);
      if (!url) return null;
      const fileName = url.split('/').pop().split('?')[0];
      return {
        file: fileName,
        roles: ['main'],
        order: index + 1,
        url,
        isVideo: false,
        alt: alt || `${title || 'Product'} - ${index + 1}`,
        srcset: `${url}?width=400 400w, ${url}?width=800 800w, ${url}?width=1200 1200w, ${url}?width=1600 1600w`,
        sizes: "(max-width: 991px) 100vw, 50vw"
      };
    };
    const buildVideo = (m, index, title) => {
      const rawSources = Array.isArray(m.sources) ? m.sources : [];
      const sources = rawSources.map((s) => {
        if (!s || !s.url) return null;
        return {
          url: toHttps(s.url),
          type: s.mime_type || s.type || null
        };
      }).filter(Boolean);

      // Prefer an MP4 URL when available (broadest support) as a single-url fallback.
      const pickPreferredUrl = () => {
        for (let i = 0; i < sources.length; i++) {
          const u = sources[i]?.url;
          if (u && u.split('?')[0].toLowerCase().endsWith('.mp4')) return u;
        }
        return sources[0]?.url || toHttps(m.src) || null;
      };

      const url = pickPreferredUrl();
      if (!url) return null;
      const fileName = url.split('/').pop().split('?')[0];

      return {
        file: fileName,
        roles: ['main'],
        order: index + 1,
        url,
        isVideo: true,
        sources: sources.length ? sources : undefined
      };
    };

    // 1) window.product.media
    try {
      if (window.product && Array.isArray(window.product.media) && window.product.media.length) {
        const title = window.product.title || 'Product';
        const mapped = window.product.media.map((m, i) => {
          return m.media_type === 'video' ? buildVideo(m, i, title) : buildImage(m.src, i, m.alt, title);
        }).filter(Boolean);
        if (mapped.length) return mapped;
      }
    } catch (e) {
      console.warn('Error reading window.product media', e);
    }

    // 2) Dawn ProductJson-<section> script
    try {
      const el = document.querySelector('script[type="application/json"][id^="ProductJson-"]');
      if (el) {
        const productData = JSON.parse(el.textContent);
        if (Array.isArray(productData?.media) && productData.media.length) {
          const title = productData.title || 'Product';
          const mapped = productData.media.map((m, i) => {
            return m.media_type === 'video' ? buildVideo(m, i, title) : buildImage(m.src, i, m.alt, title);
          }).filter(Boolean);
          if (mapped.length) return mapped;
        }
      }
    } catch (e) {
      console.warn('Error parsing ProductJson-*', e);
    }

    // 3) Generic shopify-product-data script (fallback)
    try {
      const el = document.getElementById('shopify-product-data');
      if (el) {
        const productData = JSON.parse(el.textContent);
        if (Array.isArray(productData?.media) && productData.media.length) {
          const title = productData.title || 'Product';
          const mapped = productData.media.map((m, i) => {
            return m.media_type === 'video' ? buildVideo(m, i, title) : buildImage(m.src, i, m.alt, title);
          }).filter(Boolean);
          if (mapped.length) return mapped;
        }
      }
    } catch (e) {
      console.warn('Error parsing shopify-product-data', e);
    }

    return [];
  }

  initializeFirstSlide(swiper) {
    const firstSlide = swiper.container.querySelector('.pdd-swiper-slide.is-active');
    if (firstSlide) {
      const video = firstSlide.querySelector('video');
      if (video) {
        // Set up video for autoplay
        video.currentTime = 0;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;

        // Try to play the video
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // Initial video autoplay prevented - this is expected behavior
          });
        }
      }
    }

    // Set initial page scroll lock state
    this.updatePageScrollLock(swiper);
  }

  isVideoFile(filename) {
    const videoExtensions = ['mp4', 'webm', 'mov', 'm4v'];
    const extension = filename.split('.').pop()?.toLowerCase();
    return videoExtensions.includes(extension);
  }

  handleWindowResize() {
    // Update mobile dots class for all swipers when window is resized
    const isMobile = window.innerWidth <= 991;

    this.swipers.forEach((swiper) => {
      const showMobileDots = isMobile && swiper.dots && swiper.slides && swiper.slides.length > 1;

      if (showMobileDots) {
        swiper.container.classList.add('has-mobile-dots');
      } else {
        swiper.container.classList.remove('has-mobile-dots');
      }

      // Update page scroll lock on resize
      this.updatePageScrollLock(swiper);
    });

    if (window.innerWidth >= 992) {
      this.ensureGlobalScrollListener();
    } else {
      this.teardownGlobalScrollListener();
    }
  }

  updatePageScrollLock(swiper) {
    // Only manage scroll lock if enabled and on desktop
    if (!swiper.enablePageScrollLock) return;

    const isDesktop = window.innerWidth >= 992;
    if (!isDesktop) {
      // Always unlock on mobile
      this.unlockPageScroll();
      return;
    }

    // Desktop: lock/unlock based on current slide
    const isOnFirstSlide = swiper.currentSlide === 0;
    const isOnLastSlide = swiper.currentSlide === swiper.slides.length - 1;

    // If we moved away from last slide, cancel any pending unlock timer
    if (!isOnLastSlide && swiper.pendingUnlockTimeout) {
      clearTimeout(swiper.pendingUnlockTimeout);
      swiper.pendingUnlockTimeout = null;
    }

    if (isOnFirstSlide || isOnLastSlide || swiper.slides.length <= 1) {
      // Apply cooldown only when unlocking at the last slide; do not delay disabling.
      if (isOnLastSlide && swiper.slides.length > 1) {
        const now = Date.now();
        const lastChange = swiper.lastSlideChangeAt || now;
        const elapsed = now - lastChange;
        const remaining = this.scrollDebounceTime - elapsed;

        if (remaining > 0) {
          // Schedule a delayed unlock; keep lock in the meantime
          if (swiper.pendingUnlockTimeout) {
            clearTimeout(swiper.pendingUnlockTimeout);
          }
          swiper.pendingUnlockTimeout = setTimeout(() => {
            this.unlockPageScroll();
            swiper.pendingUnlockTimeout = null;
          }, remaining);
          return;
        }
      }

      // Unlock scroll when on first slide, last slide, or only one slide
      // This allows page scroll up on first slide and down on last slide
      if (swiper.pendingUnlockTimeout) {
        clearTimeout(swiper.pendingUnlockTimeout);
        swiper.pendingUnlockTimeout = null;
      }
      this.unlockPageScroll();
    } else {
      // Lock scroll when on middle slides
      if (swiper.pendingUnlockTimeout) {
        clearTimeout(swiper.pendingUnlockTimeout);
        swiper.pendingUnlockTimeout = null;
      }
      this.lockPageScroll();
    }
  }

  setupGlobalScrollHandler(swiper) {
    this.swiperScrollTargets.add(swiper);
    this.ensureGlobalScrollListener();
  }

  ensureGlobalScrollListener() {
    if (window.innerWidth < 992) {
      return;
    }

    if (!this.globalScrollHandler) {
      this.globalScrollHandler = (event) => this.handleWheelEvent(event);
      document.addEventListener('wheel', this.globalScrollHandler, { passive: false });
    }
  }

  teardownGlobalScrollListener() {
    if (this.globalScrollHandler) {
      document.removeEventListener('wheel', this.globalScrollHandler);
      this.globalScrollHandler = null;
    }
    this.currentWheelSwiper = null;
    this.scrollAccumulator = 0;
    this.lastScrollTime = 0;
  }

  getActiveSwiperForScroll() {
    if (!this.swiperScrollTargets.size) return null;

    let bestCandidate = null;
    let bestVisible = 0;
    const viewportHeight = window.innerHeight;

    this.swiperScrollTargets.forEach((swiper) => {
      if (!swiper || !swiper.container) return;
      const gallery = swiper.galleryElement || swiper.container;
      const rect = gallery.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      if (visibleHeight > bestVisible) {
        bestVisible = visibleHeight;
        bestCandidate = { swiper, rect, visibleHeight };
      }
    });

    if (!bestCandidate || bestVisible < this.minVisiblePixels) {
      return null;
    }

    return bestCandidate;
  }

  handleWheelEvent(e) {
    if (window.innerWidth < 992) return;

    const candidate = this.getActiveSwiperForScroll();
    if (!candidate) return;

    const { swiper, rect } = candidate;
    if (!swiper.enablePageScrollLock || swiper.slides.length <= 1) {
      return;
    }

    if (this.currentWheelSwiper !== swiper) {
      this.currentWheelSwiper = swiper;
      this.scrollAccumulator = 0;
      this.lastScrollTime = 0;
    }

    const viewportHeight = window.innerHeight;
    const heroVisible = rect.bottom > 0 && rect.top < viewportHeight;
    if (!heroVisible) {
      this.scrollAccumulator = 0;
      return;
    }

    const pageAtTop = window.scrollY <= this.pageTopTolerance;

    const isOnLastSlide = swiper.currentSlide === swiper.slides.length - 1;
    const isOnFirstSlide = swiper.currentSlide === 0;
    const scrollingDown = e.deltaY > 0;
    const scrollingUp = e.deltaY < 0;

    if (isOnLastSlide && scrollingDown) {
      this.scrollAccumulator = 0;
      return; // allow page scroll down once swiper is exhausted
    }

    if (isOnLastSlide && scrollingUp) {
      if (!pageAtTop) {
        this.scrollAccumulator = 0;
        return; // let page keep moving until we are truly at the top
      }

      e.preventDefault();
      e.stopPropagation();

      const now = Date.now();
      this.scrollAccumulator += Math.abs(e.deltaY);

      if (this.scrollAccumulator >= this.scrollThreshold &&
          (now - this.lastScrollTime) > this.scrollDebounceTime) {
        this.prevSlide(swiper);
        this.scrollAccumulator = 0;
        this.lastScrollTime = now;
      }
      return;
    }

    if (isOnFirstSlide && scrollingUp) {
      this.scrollAccumulator = 0;
      return; // let page move up when we're back at slide 1
    }

    if (isOnFirstSlide && scrollingDown) {
      e.preventDefault();
      e.stopPropagation();

      const now = Date.now();
      this.scrollAccumulator += Math.abs(e.deltaY);

      if (this.scrollAccumulator >= this.scrollThreshold &&
          (now - this.lastScrollTime) > this.scrollDebounceTime) {
        this.nextSlide(swiper);
        this.scrollAccumulator = 0;
        this.lastScrollTime = now;
      }
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    this.scrollAccumulator += Math.abs(e.deltaY);

    if (this.scrollAccumulator >= this.scrollThreshold &&
        (now - this.lastScrollTime) > this.scrollDebounceTime) {
      if (scrollingDown) {
        this.nextSlide(swiper);
      } else if (scrollingUp) {
        this.prevSlide(swiper);
      }

      this.scrollAccumulator = 0;
      this.lastScrollTime = now;
    }
  }

  lockPageScroll() {
    if (this.isScrollLocked) return;

    // Store current scroll position
    this.scrollLockPosition = window.scrollY;

    // Apply scroll lock styles to body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollLockPosition}px`;
    document.body.style.width = '100%';

    this.isScrollLocked = true;
  }

  unlockPageScroll() {
    if (!this.isScrollLocked) return;

    // Remove scroll lock styles
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position
    if (this.scrollLockPosition !== undefined) {
      window.scrollTo(0, this.scrollLockPosition);
    }

    this.isScrollLocked = false;
  }

  /**
   * Cleanup and destroy a swiper instance.
   * Disconnects observers and removes event listeners to prevent memory leaks.
   */
  destroySwiper(productId) {
    const swiper = this.swipers.get(productId);
    if (!swiper) return;

    this.disableScrollHijack(swiper);

    this.swiperScrollTargets.delete(swiper);
    if (this.swiperScrollTargets.size === 0) {
      this.teardownGlobalScrollListener();
    }

    // Unlock page scroll if locked
    this.unlockPageScroll();

    // Remove from maps
    this.swipers.delete(productId);
    this.currentVariants.delete(productId);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productMediaSwiper = new ProductMediaSwiper();
  });
} else {
  window.productMediaSwiper = new ProductMediaSwiper();
}
